import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { Message } from 'src/portaljs/models/message';
import { Notification, NotificationAction, NotificationResponse } from 'src/portaljs/models/notification';
import { EnvironmentService } from './environment.service';
import * as _ from 'lodash-es';
import { ConsoleLogger } from '../shared/console-logger';
import { MessageType } from 'src/portaljs/constants/message-type';
import { ChannelService } from './channel.service';
import { DebugMessage } from '../models/portal-debug-message';
import { AuthService } from './auth.service';
import { PortalApp } from '../shared/portal-app';
import { ClientStateService } from './client-state.service';
import AuthType from '../enums/auth.type.enum';
import { UserEvent } from 'src/portaljs/constants/user-event';
import { UserStateEvent } from 'src/portaljs/models/user-state-event';

@Injectable({ providedIn: 'root' })
export class PortalMessagingService {
  private _messagesRcvd: BehaviorSubject<Message> = new BehaviorSubject<Message>(undefined);
  private messagesRcvd$: Observable<Message> = this._messagesRcvd.asObservable().pipe(filter((m) => m && m.destination === Application.PORTAL));
  private messageId = 1000;

  constructor(private envService: EnvironmentService, private channelService: ChannelService, private authService: AuthService, private clientStateService: ClientStateService) {
    window.addEventListener('message', (ev: MessageEvent) => {
      const msg: Message = ev.data;
      if (!msg.portalId || msg.portalId === this.channelService.getPortalId()) { // At some point, portalId should always be defined on all messages
        this._messagesRcvd.next(msg);
      }
    }, false);
  }


  public getNextMessageId() {
    this.messageId = this.messageId + 1;
    return String(this.messageId);
  }


  /**
   * PortalStateService.postMessage() will send messages and expect those messages to be acknowledged by the embedded app.
   * PortalMessagingService.postMessage() just sends the message w/o an expectation of an acknowledgment.
   */
  public postMessage(message: Message) {
    let win: Window = window;
    if (message.destination !== Application.PORTAL) {
      win = undefined;
      const elem: HTMLIFrameElement = document.getElementById(message.destination) as HTMLIFrameElement;
      if (elem) {
        win = elem.contentWindow;
      }
    }

    if (win) {
      const url = this.envService.getUrl(message.destination);
      const origin = new URL(url).origin;
      if (!message.id) {
        message.id = this.getNextMessageId();
      };

      if (!message.portalId) {
        message.portalId = this.channelService.getPortalId();
      }
      const mtype = message.id ? message.type + '[' + message.id + ']' : message.type;
      ConsoleLogger.debug('Sent \'' + mtype + '\' to [' + message.destination + ']: ' + JSON.stringify(message.message));
      win.postMessage(message, origin);

      if (this.envService.isMessageDebuggingEnabled()) {
        const userGuid = this.authService.getUserLogin()?.getUserGuid() || '';
        const debugMessage: DebugMessage = new DebugMessage(message.id, Application.PORTAL, message.destination, message.type, message.message, this.authService.getDebugParams());
        this.envService.addDebugMessage(userGuid, debugMessage);
      }
    }
  }

  public getMessagesRcvd$(): Observable<Message> {
    return this.messagesRcvd$;
  }

  public sendNotificationResponse(action: NotificationAction, notification: Notification) {
    if (notification.source) {
      const notificationWithoutSource = _.cloneDeep(notification);
      delete notificationWithoutSource.source;  // 'source' is used locally by the Portal. No need to send back to embedded app.

      const notificationResponse: NotificationResponse = {action: action, notification: notificationWithoutSource};
      const msg: Message = new Message( notification.id, MessageType.notification, Application.PORTAL, notification.source, notificationResponse );
      this.postMessage(msg);
    }
  }



  /** --------------------------------------------------------------------------------------------------- */
  public postAppMessage(portalApp: PortalApp, message: Message, sendIfNotReady: boolean = false) {
    this.clientStateService.addUnackMessage(message);
    if (portalApp.isReadyResponseReceived() || sendIfNotReady) {
      this.postMessage(message);
    }
  }

  public sendUserStateChangeMessages(userEvent: UserEvent, portalApp: PortalApp, authCode: string, continuationState?: any) {
    const msgId = this.getNextMessageId();
    const userState: UserStateEvent = {event: userEvent, continuationState: continuationState, authCode: authCode };
    const msg = new Message(msgId, MessageType.userStateChange, Application.PORTAL, portalApp.app, userState);
    this.postAppMessage(portalApp, msg);
    portalApp.setReadyResponseReceived(false);
  }

  public sendTenantChangeMessages(portalApp: PortalApp, authCode: string) {
    const msgId = this.getNextMessageId();
    const userState = {appId: this.authService.getDistrictAppId(), siteGuid:this.authService.getSiteGuid(), authCode: authCode};
    const msg = new Message(msgId, MessageType.tenantChange, Application.PORTAL, portalApp.app, userState);
    this.postAppMessage(portalApp, msg, true); // Current logic requires sending the tenantChange message even if the app is not ready. This should probably be fixed in the future.
    portalApp.setReadyResponseReceived(false);
  }

  public sendStateMessages(portalApp: PortalApp, authCode: string, authType: AuthType, continuationState?: any) {
    if (authType === AuthType.LOGIN || authType === AuthType.LOGOUT) {
      const userEvent: UserEvent = (authType === AuthType.LOGIN) ? UserEvent.LOGIN : UserEvent.LOGOUT;
      this.sendUserStateChangeMessages(userEvent, portalApp, authCode, continuationState);
    } else if (authType === AuthType.SITE_SWITCH) {
      this.sendTenantChangeMessages(portalApp, authCode);
    }
  }
}
